import React, { useEffect, useRef, useState } from "react";
import styles from "./Agenda.module.css";
import { CalendarAgenda } from "../../components";
import { ChatWidget } from "../../components";
import { TenantSetupModal } from "../../components";
import { CoexistenceLimitationsModal } from "../../components";
import api from "../../services/api";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useFacebookSDK } from "../../hooks/useFacebookSDK";
import { useAuth } from "../../auth/AuthContext";

// Origens exatas das quais o popup do Embedded Signup envia o postMessage.
// Allowlist explícita evita match com domínios hostis (ex.: evilfacebook.com).
const FB_ALLOWED_ORIGINS = [
  "https://www.facebook.com",
  "https://web.facebook.com",
  "https://business.facebook.com",
];

// Função auxiliar para barras de progresso dentro do menu
const UsageBar = ({ label, used, total }) => {
  const percentage = Math.min((used / total) * 100, 100);
  const isHigh = percentage > 85;

  return (
    <div className={styles.usage}>
      <div className={styles.usageRow}>
        <span>{label}</span>
        <span>
          {used}/{total}
        </span>
      </div>
      <div className={styles.usageTrack}>
        <div
          className={`${styles.usageFill} ${isHigh ? styles.usageFillHigh : ""}`}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
};

const Agenda = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [loading, setLoading] = useState(false);
  const [businessData, setBusinessData] = useState(null);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [isSetupModalOpen, setIsSetupModalOpen] = useState(false);
  // Fase 4 / Passo 2A: controla a checklist de limitações exibida antes de
  // lançar o onboarding de coexistência (o FB.login só dispara no confirm).
  const [isCoexistenceModalOpen, setIsCoexistenceModalOpen] = useState(false);
  const profileRef = useRef();
  // Guarda os dados de sessão do Embedded Signup (phone_number_id, waba_id)
  // emitidos pela Meta via postMessage durante o fluxo "code" v4.
  const sessionInfoRef = useRef(null);
  // Guarda o nome do evento de conclusão emitido pelo Embedded Signup
  // (ex.: "FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING" no fluxo de coexistence).
  // O backend usa esse evento para escolher o caminho de coexistence.
  const coexistenceEventRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setShowProfileMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const [searchParams, setSearchParams] = useSearchParams();

  useFacebookSDK(process.env.REACT_APP_META_APP_ID, {
    cookie: true,
    xfbml: true,
  });

  // Captura o evento de sessão do WhatsApp Embedded Signup (fluxo "code" v4).
  // A Meta envia, via postMessage, um payload com phone_number_id e waba_id
  // que o backend usa para localizar o número certo. Guardamos em ref para
  // anexar à chamada de conexão sem disparar re-render.
  useEffect(() => {
    const handleSignupMessage = (event) => {
      // Só confiamos em mensagens originadas no domínio do Facebook.
      if (!FB_ALLOWED_ORIGINS.includes(event.origin)) return;

      try {
        const data =
          typeof event.data === "string" ? JSON.parse(event.data) : event.data;

        if (data?.type === "WA_EMBEDDED_SIGNUP") {
          // data.data pode conter phone_number_id e waba_id.
          sessionInfoRef.current = data.data ?? null;
          // data.event traz o nome do evento de conclusão; no fluxo de
          // coexistence chega como "FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING".
          coexistenceEventRef.current = data.event ?? null;
        }
      } catch {
        // Mensagens não-JSON (ruído de outras libs) são ignoradas.
      }
    };

    window.addEventListener("message", handleSignupMessage);
    return () => window.removeEventListener("message", handleSignupMessage);
  }, []);

  // 2. Função de Conexão (Embedded Signup)
  const handleConnectWhatsApp = () => {
    if (!window.FB) return alert("O sistema da Meta ainda está carregando. Tente novamente em instantes.");

    // Limpa qualquer dado de sessão de uma tentativa anterior.
    sessionInfoRef.current = null;
    coexistenceEventRef.current = null;

    // Facebook Login for Business v4 — fluxo "code".
    // O backend troca esse code por um token; o front nunca toca no token.
    window.FB.login(
      (response) => {
        const code = response?.authResponse?.code;
        if (code) {
          connectWhatsApp(code);
        } else {
          // Usuário fechou o popup ou negou a conexão.
          setLoading(false);
          alert("Conexão com o WhatsApp cancelada. Você pode tentar de novo quando quiser.");
        }
      },
      {
        config_id: process.env.REACT_APP_WHATSAPP_CONFIGURATION_ID,
        response_type: "code",
        override_default_response_type: true,
        extras: {},
      },
    );
  };

  // 2b. Conexão em COEXISTENCE (Fase 1) — onboarding do app do WhatsApp Business.
  // Mantém o número ativo no celular do cliente e, ao mesmo tempo, conecta na
  // Cloud API. Usa um config_id DEDICADO (ADR-0015), provisionado pelo Guto no
  // painel da Meta. O backend identifica o fluxo pelo evento de conclusão
  // ("FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING") capturado via postMessage.
  // CTA de coexistence: NÃO lança o FB.login direto. Primeiro valida os guards
  // (SDK carregado + config_id dedicado) e então abre a checklist de limitações
  // (Fase 4 / Passo 2A). O onboarding só dispara quando o usuário confirma.
  const handleConnectWhatsAppCoexistence = () => {
    if (!window.FB)
      return alert(
        "O sistema da Meta ainda está carregando. Tente novamente em instantes.",
      );

    const coexistenceConfigId =
      process.env.REACT_APP_WHATSAPP_COEXISTENCE_CONFIGURATION_ID;
    if (!coexistenceConfigId) {
      // Sem config_id dedicado o fluxo de coexistence não lança.
      return alert(
        "A conexão de conta existente ainda não está disponível. Fale com o suporte para habilitar.",
      );
    }

    // Guards ok → abre a checklist. A conexão de fato roda no confirm.
    setIsCoexistenceModalOpen(true);
  };

  // Fechar a checklist sem conectar (cancelar / ESC / clique no overlay).
  const handleCancelCoexistence = () => {
    setIsCoexistenceModalOpen(false);
  };

  // Confirmação da checklist → executa o onboarding de coexistência (FB.login).
  // Corpo movido de `handleConnectWhatsAppCoexistence`. Os guards já passaram
  // antes de abrir o modal; o config_id é relido aqui por segurança.
  const handleConfirmCoexistence = () => {
    setIsCoexistenceModalOpen(false);

    // Fase 4 (2A): registra o aceite das limitações no backend. Não pode
    // bloquear o fluxo de FB.login — se falhar, só logamos e seguimos.
    try {
      const user = JSON.parse(localStorage.getItem("user"));
      const tenantId = user?.tenantId;
      if (tenantId) {
        api
          .post(`/tenants/${tenantId}/accept-coexistence-limitations`, {})
          .catch((err) => {
            console.error(
              "Não foi possível registrar o aceite das limitações de coexistência:",
              err,
            );
          });
      }
    } catch (err) {
      console.error("Falha ao preparar o registro de aceite:", err);
    }

    const coexistenceConfigId =
      process.env.REACT_APP_WHATSAPP_COEXISTENCE_CONFIGURATION_ID;
    if (!window.FB || !coexistenceConfigId) {
      // Defesa em profundidade: se o estado mudou entre abrir e confirmar.
      return alert(
        "A conexão de conta existente ficou indisponível. Tente novamente em instantes.",
      );
    }

    // Limpa dados de tentativas anteriores.
    sessionInfoRef.current = null;
    coexistenceEventRef.current = null;

    // Doc da Meta confirmada (export v3 + changelog): o `featureType` vai DENTRO
    // do objeto `extras` do FB.login (não no config_id), e o valor que lança o
    // onboarding do app do WhatsApp Business (coexistence) é
    // `whatsapp_business_app_onboarding` (o antigo `coexistence` foi descontinuado).
    // Este é o padrão do caminho de Coexistence — sem flag de fallback.
    const extras = {
      featureType: "whatsapp_business_app_onboarding",
      sessionInfoVersion: "3",
    };

    window.FB.login(
      (response) => {
        const code = response?.authResponse?.code;
        if (code) {
          connectWhatsApp(code);
        } else {
          setLoading(false);
          alert(
            "Conexão da conta existente cancelada. Você pode tentar de novo quando quiser.",
          );
        }
      },
      {
        config_id: coexistenceConfigId,
        response_type: "code",
        override_default_response_type: true,
        extras,
      },
    );
  };

  //3. Conexão com o Instagram
  const handleConnectInstagram = () => {
    if (!window.FB)
      return alert(
        "O SDK da Meta ainda está carregando. Tente novamente em 2 segundos.",
      );

    window.FB.login(
      (response) => {
        if (response.authResponse) {
          const accessToken = response.authResponse.accessToken;
          saveInstagramToken(accessToken);
        }
      },
      {
        // Escopos exatos para Instagram profissional
        scope:
          "instagram_basic,instagram_manage_messages,pages_manage_metadata,pages_show_list,pages_read_engagement",
        return_scopes: true,
      },
    );
  };

  // 3. Conectar no Banco (Vinculado ao Tenant atual) — envia o code do fluxo v4
  const connectWhatsApp = async (code) => {
    setLoading(true);
    try {
      const user = JSON.parse(localStorage.getItem("user"));
      const tenantId = user?.tenantId;

      const res = await api.post(`/tenants/${tenantId}/connect-whatsapp`, {
        code,
        sessionInfo: sessionInfoRef.current ?? undefined,
        // Quando presente, sinaliza ao backend o caminho de coexistence
        // (ex.: "FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING").
        event: coexistenceEventRef.current ?? undefined,
      });

      if (res.data?.status === "coexistence_connected") {
        alert(
          "Conta conectada em coexistência! Você pode seguir usando o app do WhatsApp Business normalmente.",
        );
      } else if (res.data?.status === "pending_number") {
        alert(
          "Conta conectada! Falta configurar o número do WhatsApp — você pode finalizar isso pelo painel da Meta.",
        );
      } else {
        alert("WhatsApp conectado com sucesso!");
      }
    } catch (error) {
      console.error("Erro ao conectar o WhatsApp:", error);
      alert(
        "Não conseguimos conectar o seu WhatsApp agora. Tente novamente em alguns instantes.",
      );
    } finally {
      setLoading(false);
    }
  };

  const saveInstagramToken = async (accessToken) => {
    setLoading(true);
    try {
      const user = JSON.parse(localStorage.getItem("user"));
      const tenantId = user?.tenantId;

      await api.post(`/tenants/${tenantId}/connect-instagram`, {
        accessToken,
      });
      alert("Instagram conectado com sucesso!");
    } catch (error) {
      console.error("Erro ao vincular Instagram:", error);
      alert("Erro ao vincular Instagram.");
    } finally {
      setLoading(false);
    }
  };

  // Intent de conexão vindo da Sidebar global (?connect=whatsapp|coexistence|
  // instagram). A Sidebar agora vive no AppLayout; como o fluxo de conexão
  // (FB SDK, modais, refs) mora nesta tela, o item navega para /agenda com o
  // intent e nós o disparamos ao montar/receber o param.
  useEffect(() => {
    const connect = searchParams.get("connect");
    if (!connect) return;
    // Limpa o param para não re-disparar em re-render/refresh.
    setSearchParams({}, { replace: true });
    if (connect === "whatsapp") handleConnectWhatsApp();
    else if (connect === "coexistence") handleConnectWhatsAppCoexistence();
    else if (connect === "instagram") handleConnectInstagram();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Intent de abrir o wizard, vindo do banner de cadastro incompleto do
  // AppLayout (`/agenda?setup=true`). Mesmo formato do intent de conexão acima:
  // efeito PRÓPRIO, dependente de `searchParams`.
  //
  // ⚠️ Isto já morou dentro do efeito de `fetchBusinessInfo`, que depende só de
  // `[navigate]` e portanto roda uma vez por MONTAGEM. O CTA do banner navega
  // client-side para a mesma rota — a Agenda não remonta —, então o clique
  // mudava a URL e não abria nada. Carga completa funcionava, e é por isso que
  // passou despercebido. Como a abertura automática saiu (ver abaixo), o banner
  // é o ÚNICO caminho restante: mudo, ele deixava o tenant sem segmento sem
  // nenhuma forma de configurar o segmento pela UI.
  //
  // Sobre a abertura automática: tenant sem `segment` abria o modal sozinho ao
  // entrar na Agenda. Isso é bloqueio de tela disfarçado de ajuda — o signup
  // por e-mail/senha cria o tenant SEM segmento (`authController.ts:168-177`),
  // então a base inteira levava um modal na cara toda vez que abria a própria
  // agenda, sem ter pedido nada. Virou banner não-bloqueante (ver
  // `needsOnboarding` em `auth/onboarding.js`); o modal só aparece a quem clica.
  useEffect(() => {
    if (searchParams.get("setup") !== "true") return;
    // Remove só a chave `setup`, preservando qualquer outro param — um
    // `setSearchParams({})` cru apagaria o `connect` que o efeito acima trata.
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("setup");
        return next;
      },
      { replace: true },
    );
    setIsSetupModalOpen(true);
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    const fetchBusinessInfo = async () => {
      try {
        const response = await api.get("/tenants/me");
        setBusinessData(response.data);
      } catch (error) {
        if (error.response?.status === 401) {
          navigate("/login", { replace: true });
          return;
        }
        console.error("Erro ao carregar info do negócio", error);
      }
    };
    fetchBusinessInfo();
  }, [navigate]);

  const handleSetupSuccess = async (_updatedTenant) => {
    setIsSetupModalOpen(false);
    try {
      const response = await api.get("/tenants/me");
      setBusinessData(response.data);
    } catch (_) {
      // fallback silencioso — businessData continuará com dados anteriores
    }
  };

  return (
    <>
      <section className={styles.agenda}>
        <div className={styles.header}>
          <div>
            <h2 className={styles.headTitle}>Minha agenda</h2>
            <p className={styles.headSubtitle}>Gerencie seus compromissos</p>
          </div>
          <div className={styles.agendaHeader} ref={profileRef}>
            {/* Indicadores rápidos de conexão (Bolinhas) */}
            <div className={styles.connections}>
              {/* Fase 4 (2C): badge de coexistência + progresso de importação.
                  Só aparece para tenants em modo coexistência. Tudo
                  optional-chained — não quebra para tenants tradicionais. */}
              {(businessData?.tenant?.coexistence ||
                businessData?.tenant?.isOnBizApp) &&
                (() => {
                  const t = businessData?.tenant;
                  const connected = t?.connected;
                  const isDisconnected = connected === false;
                  const syncStatus = t?.historySyncStatus;
                  const syncProgress = t?.historySyncProgress;
                  const syncPhase = t?.historySyncPhase;

                  const phaseLabel =
                    syncPhase === 0
                      ? "dia 0-1"
                      : syncPhase === 1
                        ? "dia 1-90"
                        : syncPhase === 2
                          ? "dia 90-180"
                          : null;

                  // Status que indicam importação em andamento (barra).
                  const inProgress =
                    syncStatus &&
                    syncStatus !== "completed" &&
                    syncStatus !== "declined" &&
                    syncStatus !== "pending";

                  return (
                    <div className={styles.coexBlock}>
                      <span
                        title={
                          isDisconnected
                            ? "Desconectada — reconecte pelo app do WhatsApp Business"
                            : "Conta em coexistência — você segue usando o app do WhatsApp Business normalmente"
                        }
                        className={`${styles.coexBadge} ${
                          isDisconnected ? styles.coexBadgeOff : ""
                        }`}
                      >
                        <span
                          className={`${styles.coexDot} ${
                            isDisconnected ? styles.coexDotOff : ""
                          }`}
                        />
                        Coexistência
                      </span>

                      {/* Progresso de importação do histórico */}
                      {inProgress && (
                        <div title="Importando histórico de conversas">
                          <div className={styles.coexProgressLabel}>
                            Importando histórico:{" "}
                            {typeof syncProgress === "number"
                              ? `${syncProgress}%`
                              : "..."}
                            {phaseLabel ? ` (${phaseLabel})` : ""}
                          </div>
                          <div className={styles.coexBar}>
                            <div
                              className={styles.coexBarFill}
                              style={{
                                width: `${Math.min(
                                  Math.max(
                                    typeof syncProgress === "number"
                                      ? syncProgress
                                      : 0,
                                    0,
                                  ),
                                  100,
                                )}%`,
                              }}
                            />
                          </div>
                        </div>
                      )}

                      {syncStatus === "completed" && (
                        <span
                          title="Histórico de conversas importado"
                          className={styles.coexDone}
                        >
                          ✓ Histórico importado
                        </span>
                      )}

                      {syncStatus === "declined" && (
                        <span
                          title="Você optou por não compartilhar o histórico"
                          className={styles.coexMuted}
                        >
                          Histórico não compartilhado
                        </span>
                      )}

                      {syncStatus === "pending" && (
                        <span
                          title="Aguardando início da importação do histórico"
                          className={styles.coexMuted}
                        >
                          Aguardando histórico
                        </span>
                      )}
                    </div>
                  );
                })()}

              <div
                className={styles.channel}
                title={
                  businessData?.tenant.metaPhoneNumberId
                    ? "WhatsApp conectado"
                    : "WhatsApp desconectado"
                }
              >
                <div
                  className={`${styles.channelDot} ${
                    businessData?.tenant.metaPhoneNumberId
                      ? styles.channelDotWhats
                      : ""
                  }`}
                />
                <span className={styles.channelLabel}>WA</span>

                {/* Afordância de reconexão (#5.3). Aparece só quando o
                    WhatsApp está desconectado (sem metaPhoneNumberId).
                    • Coexistência: a reconexão é feita pelo próprio app do
                      WhatsApp Business — apenas orientamos (sem CTA ativo).
                    • Tradicional: liga ao fluxo de Embedded Signup já
                      existente (handleConnectWhatsApp). */}
                {!businessData?.tenant?.metaPhoneNumberId &&
                  (businessData?.tenant?.coexistence ? (
                    <span
                      title="Reconecte pelo app do WhatsApp Business"
                      className={styles.reconnectHint}
                    >
                      reconecte pelo app
                    </span>
                  ) : (
                    <button
                      type="button"
                      onClick={handleConnectWhatsApp}
                      title="Reconectar o WhatsApp"
                      className={styles.reconnectBtn}
                    >
                      Reconectar
                    </button>
                  ))}
              </div>

              <div
                className={styles.channel}
                title={
                  businessData?.tenant.instagramAccountId
                    ? "Instagram conectado"
                    : "Instagram desconectado"
                }
              >
                <div
                  className={`${styles.channelDot} ${
                    businessData?.tenant.instagramAccountId
                      ? styles.channelDotInsta
                      : ""
                  }`}
                />
                <span className={styles.channelLabel}>IG</span>
              </div>
            </div>
            {/* Botão do Perfil que abre o Menu */}
            <div
              onClick={() => setShowProfileMenu(!showProfileMenu)}
              className={styles.profile}
            >
              <div className={styles.profileText}>
                <p className={styles.profileName}>
                  {businessData?.tenant?.name || "Meu Negócio"}
                </p>
                <p className={styles.profilePlan}>
                  Plano {businessData?.plan?.name || "Carregando..."}
                </p>
              </div>
              <div className={styles.avatar}>
                {businessData?.tenant?.name?.charAt(0).toUpperCase()}
              </div>
            </div>

            {/* Dropdown de Assinatura e Status */}
            {showProfileMenu && (
              <div className={styles.dropdown}>
                <p className={styles.dropdownTitle}>Status do plano</p>

                {/* Aqui usaremos os dados que virão daquela rota de Usage futuramente */}
                <UsageBar
                  label="Mensagens chatbot"
                  used={businessData?.usage?.messagesSent || 0}
                  total={businessData?.plan?.messagesLimit || 100}
                />
                <UsageBar
                  label="Agendamentos"
                  used={businessData?.usage?.appointmentsCount || 0}
                  total={500}
                />

                <div className={styles.dropdownFooter}>
                  <button
                    onClick={() => navigate("/business/settings")}
                    className={styles.manageBtn}
                  >
                    Gerenciar assinatura
                  </button>
                  <button
                    className={styles.logoutBtn}
                    onClick={() => {
                      // INV-F2: `logout()` do AuthContext já chama
                      // `clearSession()` (via authService) e zera o estado
                      // React. Os `removeItem` que existiam aqui eram um
                      // conjunto divergente — removidos.
                      logout();
                      navigate("/login");
                    }}
                  >
                    Sair
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {loading ? (
          <div className={styles.loader}>
            <p>Sincronizando com a Meta...</p>
          </div>
        ) : (
          <CalendarAgenda />
        )}
      </section>
      {isSetupModalOpen && (
        <TenantSetupModal
          onSuccess={handleSetupSuccess}
          onClose={() => setIsSetupModalOpen(false)}
        />
      )}
      <CoexistenceLimitationsModal
        isOpen={isCoexistenceModalOpen}
        onConfirm={handleConfirmCoexistence}
        onCancel={handleCancelCoexistence}
      />
      {/* TODO Fase 4 (2C): exibir badge/painel de progresso da coexistência. */}
      <ChatWidget />
    </>
  );
};

export default Agenda;
